export default function BlogContent() {
  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <article className="prose prose-lg max-w-none">
          <div className="text-gray-700 leading-relaxed space-y-6">
            <p className="text-xl text-gray-600 font-medium">
              In a tight 2026 labor market, finding a qualified AutoCAD expert who actually understands BC formwork is nearly impossible. A "bad hire" who doesn't understand concrete sequencing can lead to thousands of dollars in site rework.
            </p>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-error-warning-line text-white text-xl"></i>
                </span>
                The Cost of a "Bad Hire"
              </h2>
              <p className="text-gray-700 mb-4">
                Hiring the wrong person for your layout and detailing work creates cascading problems:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Recruitment Costs:</strong> 3-6 months of job postings, interviews, and background checks</li>
                <li><strong>Training Investment:</strong> 2-3 months before they're productive (if they ever are)</li>
                <li><strong>Site Rework:</strong> $5,000-$15,000 per error when drawings don't match field conditions</li>
                <li><strong>Schedule Delays:</strong> Missed concrete pours cost $2,000-$5,000 per day in idle labor</li>
                <li><strong>Material Waste:</strong> Incorrect takeoffs lead to over-ordering or emergency re-orders</li>
                <li><strong>Reputation Damage:</strong> Clients lose confidence when your drawings are unreliable</li>
              </ul>
              <p className="text-gray-700 mt-4 mb-0">
                <strong>Total Cost of a Bad Hire:</strong> $50,000-$100,000+ when you factor in all the downstream impacts.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-search-eye-line text-white text-xl"></i>
                </span>
                The Hiring Challenge in 2026
              </h2>
              <p className="text-gray-700 mb-4">
                Vancouver's construction labor market is brutally competitive. Finding someone who checks all these boxes is nearly impossible:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li><strong>AutoCAD Proficiency:</strong> Not just basic drafting—advanced 3D modeling and precision detailing</li>
                <li><strong>Bluebeam Expertise:</strong> Digital takeoffs, markup workflows, and PDF coordination</li>
                <li><strong>Concrete Knowledge:</strong> Understanding of formwork systems, pour sequences, and shoring</li>
                <li><strong>BC Building Code:</strong> Familiarity with local regulations and inspection requirements</li>
                <li><strong>Field Experience:</strong> Knowing how crews actually build what you're drawing</li>
                <li><strong>Communication Skills:</strong> Ability to coordinate with engineers, architects, and site supervisors</li>
              </ul>
              <p className="text-gray-700 mt-4 mb-0">
                Even if you find this unicorn candidate, they're likely fielding multiple offers and will command top-tier compensation.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-medal-line text-white text-xl"></i>
                </span>
                Expertise on Demand: The Dinari Designs Advantage
              </h2>
              <p className="text-gray-700 mb-4">
                Rather than spending months on recruitment and training, you can immediately access <strong>20 years of specific BC construction experience</strong>. Here's what you get with <strong>Dinari Designs</strong>:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Proven Track Record:</strong> Hundreds of successful projects across the Lower Mainland</li>
                <li><strong>Deep Technical Knowledge:</strong> We understand concrete sequencing, not just CAD commands</li>
                <li><strong>Field-Tested Drawings:</strong> Our layouts work in Richmond, Burnaby, and North Vancouver conditions</li>
                <li><strong>Zero Training Time:</strong> We're productive from day one on your project</li>
                <li><strong>Quality Assurance:</strong> Every drawing is reviewed before delivery—no rookie mistakes</li>
                <li><strong>Continuous Availability:</strong> No vacation, sick days, or sudden resignations</li>
              </ul>
              <p className="text-gray-700 mt-4 mb-0">
                We don't just "draw lines"—we understand how a crew in Richmond is going to actually strip those forms. You aren't just hiring a drafter; you're hiring a <strong>technical insurance policy</strong> for your site.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-shield-check-line text-white text-xl"></i>
                </span>
                Real-World Example: The Sequencing Error
              </h2>
              <p className="text-gray-700 mb-4">
                <strong>The Scenario:</strong> A generalist drafter creates a formwork layout for a parkade slab. The drawings look clean and professional. But there's a problem: the pour sequence doesn't account for the shoring tower locations.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>The Result:</strong> The crew arrives on site and realizes they can't access half the slab area without moving shoring. The pour is delayed by two days while the layout is revised. Cost: $8,000 in idle labor + $3,000 in concrete truck standby fees.
              </p>
              <p className="text-gray-700 mb-0">
                <strong>The Dinari Difference:</strong> With 20 years of field experience, we would have caught this issue during the design phase. Our drawings account for real-world construction logistics—because we've actually been on those sites.
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-8 my-12 text-white">
              <h2 className="text-3xl font-bold mb-4">
                Ready to lean out your overhead?
              </h2>
              <p className="text-lg mb-6 text-teal-50">
                Get immediate access to <strong>20 years of BC construction expertise</strong>. No recruitment, no training, no costly mistakes. Just professional <strong>AutoCAD</strong> and <strong>Bluebeam</strong> work that's right the first time.
              </p>
              <a 
                href="/home#contact" 
                className="inline-flex items-center px-8 py-4 bg-white text-teal-600 font-semibold rounded-lg hover:bg-teal-50 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                Get a Quote
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>

            <div className="border-t border-gray-200 pt-8 mt-12">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-gray-500">Share this article:</span>
                  <div className="flex space-x-3">
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-linkedin-fill"></i>
                    </a>
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-twitter-x-fill"></i>
                    </a>
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-facebook-fill"></i>
                    </a>
                  </div>
                </div>
                <a 
                  href="/knowledge-hub" 
                  className="text-teal-600 hover:text-teal-700 font-medium flex items-center cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back to Knowledge Hub
                </a>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}
